import { useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Sparkle, FloppyDisk } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const n = (v) => (v === "" || v == null ? null : Number(String(v).replace(",", ".")));

export default function ComparativeRentEditor({ level, targetId, unitId, propertyId, value, onSaved }) {
  const [f, setF] = useState({ low: value?.low ?? "", mid: value?.mid ?? "", high: value?.high ?? "", source: value?.source || "", source_ref: value?.source_ref || "", origin: value?.origin || "manual" });
  const [sug, setSug] = useState(null);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF((c) => ({ ...c, [k]: e.target.value, origin: k === "source" || k === "source_ref" ? c.origin : "manual" }));
  const tid = `cr-${level}`;

  const fetchSug = async () => {
    setBusy(true);
    try {
      const { data } = await api.get("/rent-increase/suggestion", { params: level === "unit" ? { unit_id: unitId } : { property_id: propertyId } });
      setSug(data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Kein Vorschlag verfügbar"); }
    finally { setBusy(false); }
  };
  const adopt = () => { setF({ low: sug.low, mid: sug.mid, high: sug.high, source: sug.source, source_ref: sug.source_ref, origin: "miete-aktuell" }); setSug(null); };
  const save = async () => {
    if (!n(f.mid)) { toast.error("Mittelwert (€/m²) ist erforderlich"); return; }
    try {
      await api.put(`/rent-increase/${level === "unit" ? "units" : "properties"}/${targetId}/comparative-rent`,
        { low: n(f.low), mid: n(f.mid), high: n(f.high), source: f.source || null, source_ref: f.source_ref || null, origin: f.origin });
      toast.success(level === "unit" ? "Vergleichsmiete der Einheit gespeichert" : "Vergleichsmiete des Objekts gespeichert");
      onSaved?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Speichern fehlgeschlagen"); }
  };

  return (
    <div className="space-y-2" data-testid={`${tid}-editor`}>
      <div className="grid grid-cols-3 gap-2">
        <div><Label className="text-[11px]">Unterer Wert €/m²</Label><Input value={f.low} onChange={set("low")} inputMode="decimal" data-testid={`${tid}-low`} /></div>
        <div><Label className="text-[11px]">Mittelwert €/m² *</Label><Input value={f.mid} onChange={set("mid")} inputMode="decimal" data-testid={`${tid}-mid`} /></div>
        <div><Label className="text-[11px]">Oberer Wert €/m²</Label><Input value={f.high} onChange={set("high")} inputMode="decimal" data-testid={`${tid}-high`} /></div>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <div><Label className="text-[11px]">Quelle</Label><Input value={f.source} onChange={set("source")} placeholder="Mietspiegel Berlin 2025" data-testid={`${tid}-source`} /></div>
        <div><Label className="text-[11px]">Feld / Fundstelle</Label><Input value={f.source_ref} onChange={set("source_ref")} placeholder="Feld H4" data-testid={`${tid}-ref`} /></div>
      </div>
      {f.origin === "miete-aktuell" && <div className="text-[11px] text-amber-700">Domivus-Vorschlag übernommen — bitte durch Mietspiegel-Werte ersetzen, sobald verfügbar.</div>}
      {sug && (
        <div className="rounded border border-indigo-200 bg-indigo-50/60 p-2 text-[12px] space-y-1" data-testid={`${tid}-suggestion`}>
          <div className="font-medium text-indigo-900">Domivus-Vorschlag: {sug.low?.toFixed(2)} / <b>{sug.mid?.toFixed(2)}</b> / {sug.high?.toFixed(2)} €/m²</div>
          <div className="text-slate-600">{sug.calculation}</div>
          <div className="text-amber-700">{sug.note}</div>
          <div className="flex gap-2"><Button size="sm" onClick={adopt} data-testid={`${tid}-adopt`}>Übernehmen</Button>
            <a href={sug.source_ref} target="_blank" rel="noreferrer" className="text-indigo-700 underline self-center">Quelle öffnen</a></div>
        </div>
      )}
      <div className="flex gap-2">
        <Button size="sm" variant="outline" className="gap-1" onClick={fetchSug} disabled={busy} data-testid={`${tid}-fetch`}><Sparkle size={13} /> {busy ? "Lade…" : "Domivus-Vorschlag"}</Button>
        <Button size="sm" className="gap-1" onClick={save} data-testid={`${tid}-save`}><FloppyDisk size={13} /> Speichern</Button>
      </div>
    </div>
  );
}
